import React, { lazy, Suspense, useMemo } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Header from "../shared/header";
import Unauthorized from "../shared/Unauthorized";
import { useAppStateContext } from "../state/provider";
import {
  getToken,
  getUserInfo,
  USER_ACCESS,
  USER_ACCESS_AUTH
} from "../utils/utils";
import Protected from "./Protected";


import MainPageTypography from "../shared/typography/MainPageTypography";
import RouteLoadingBoundary from "../shared/RouteLoadingBoundary";
import "./page-styles";

const Client = lazy(() => import("../pages/Clients/Client"));
const Payment = lazy(() => import("../pages/Payment"));
const Project = lazy(() => import("../pages/Project/Project"));
const Template = lazy(() => import("../pages/Template/Template"));
const RegisterUser = lazy(() => import("../pages/UserManagement/RegisterUser"));
const UserManagement = lazy(() => import("../pages/UserManagement/UserManagement"));
const CreateInvoice = lazy(() => import("../pages/Invoice"));
const Login = lazy(() => import("../pages/Login/Login"));
const GenerateInvoice = lazy(() => import("../pages/Clients/GenerateInvoice"));
const ProjectEnquireResponse = lazy(() => import("../pages/Managers/ProjectEnquireResponse"));
const Checkout = lazy(() => import("../pages/Checkout"));
const PricingManager = lazy(() => import("../pages/Pricing"));
const FullyPaidInvoices = lazy(() => import("../pages/FullyPaidInvoices"));
const SharedStaffDashboard = lazy(() => import("../pages/Labor/SharedStaffDashboard"));
const SharedClientDashboard = lazy(() => import("../pages/Labor/SharedClientDashboard"));
const SharedLaborForm = lazy(() => import("../pages/Labor/SharedLaborForm"));
const Labor = lazy(() => import("../pages/Labor/Labor"));

// const Login = lazy(() => import('../pages/Login/Login'));
// const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
// const Invoice = lazy(() => import('../pages/Invoice/Invoice'));
// const UserManagement = lazy(() => import('../pages/UserManagement/UserManagement'));

function Routers(props: any) {
  const { state, dispatch } = useAppStateContext();
  const { isAuthenticated } = state.auth;
  const path = useLocation().pathname;
  const isSharedDashboard = ["/staff-dashboard", "/client-dashboard"].includes(path.replace(/\/+$/, "")) || path.startsWith("/labor-form/");
  const userInfo = state.auth?.user || getUserInfo();
  const navigate = useNavigate();

  const isAuthorizedByRole = (path: any) => {
    const userAccess: any = USER_ACCESS_AUTH[userInfo?.role] || [];
    if (userAccess.includes(path) || userAccess[0] === "all" || path === "/") {
      return true;
    }
    return false;
  };

  const isAuthorized = useMemo(() => isAuthorizedByRole(path), [path]);
  // const isAuthorized = isAuthorizedByRole(path);
  const _isAuthenticated = isAuthenticated || getToken();

  if (window?.location?.pathname === "/login" && getToken()) {
    return <Navigate to={"/clients"} replace />;
  }
  return (
    <MainPageTypography>
      {_isAuthenticated && !isSharedDashboard && <Header />}
      <RouteLoadingBoundary key={path}>
      <Suspense fallback={<div className="route-loading" role="status" aria-live="polite">Loading page…</div>}>
      <Routes>
        <Route path="/staff-dashboard" element={<SharedStaffDashboard />} />
        <Route path="/client-dashboard" element={<SharedClientDashboard />} />
        <Route path="/labor-form/:formKey" element={<SharedLaborForm />} />
        <Route
          path="/"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Client />
            </Protected>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/register/:uid" element={<RegisterUser />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* <Route path="/home" element={<Protected isAuthenticated={_isAuthenticated}
                    isAuthorized={isAuthorized}><Dashboard /></Protected>} /> */}
        {/* <Route
          path="/home"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Home />
            </Protected>
          }
        /> */}

        <Route
          path="/home"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Client />
            </Protected>
          }
        />


        <Route
          path="/templates"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Template />
            </Protected>
          }
        />
        <Route
          path="/pricing"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <PricingManager />
            </Protected>
          }
        />
        <Route
          path="/clients"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Client />
            </Protected>
          }
        />
        <Route
          path="/payments"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Payment />
            </Protected>
          }
        />
        <Route
          path="/projects"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <Project />
            </Protected>
          }
        />
        <Route
          path="/labor/*"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={true}
            >
              <Labor />
            </Protected>
          }
        />
        <Route
          path="/usermanagement"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <UserManagement />
            </Protected>
          }
        />
        <Route
          path="/project-inquiry-response/:client_id/:manager_email/:manager_id/:project_id"
          element={
            <Protected isAuthenticated={_isAuthenticated} isAuthorized={true}>
              <ProjectEnquireResponse />
            </Protected>
          }
        />
        {/* <Route path="/invoice" element={<CreateInvoice />} />
                    <Route path="/preview-invoice" element={<GenerateInvoice />} />
                    <Route path="/preview-invoice/:id" element={<GenerateInvoice />} />
                    <Route path="/invoice/:id/:clientId" element={<CreateInvoice />} /> */}
        <Route
          path="/invoice"
          element={
            <Protected isAuthenticated={_isAuthenticated} isAuthorized={true}>
              <CreateInvoice />
            </Protected>
          }
        />
        <Route
          path="/preview-invoice"
          element={
            <Protected isAuthenticated={_isAuthenticated} isAuthorized={true}>
              <GenerateInvoice />
            </Protected>
          }
        />
        <Route
          path="/preview-invoice/:id"
          element={
            <Protected isAuthenticated={_isAuthenticated} isAuthorized={true}>
              <GenerateInvoice />
            </Protected>
          }
        />
        <Route
          path="/invoice/:id/:clientId"
          element={
            <Protected isAuthenticated={_isAuthenticated} isAuthorized={true}>
              <CreateInvoice />
            </Protected>
          }
        />
        <Route
          path="/records"
          element={
            <Protected
              isAuthenticated={_isAuthenticated}
              isAuthorized={isAuthorized}
            >
              <FullyPaidInvoices />
            </Protected>
          }
        />
        <Route path="/checkout*" element={<Checkout />} />
      </Routes>
      </Suspense>
      </RouteLoadingBoundary>
    </MainPageTypography>
  );
}

export default Routers;

